import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useAuthMe } from "../lib/queries.js";
import { useAuthIdentity } from "../lib/omauth.js";
import { tourSeen } from "./WelcomeTour.js";
import { useRelayStatus } from "../lib/ws.js";

/**
 * Post-connect capability panel. Right after a key connects (once per key), we tell the user
 * exactly what it can and can't do. `auth/me` exposes NO entitlements list (see FRICTION.md),
 * so we show what's definitive from the key (trading via `can_execute`, type, rate budget,
 * credits) and LIVE-PROBE the two gated features — cross-venue arbitrage (`arbitrage:read`)
 * and the real-time stream (`stream:realtime`) — rather than guess.
 */

/* ------- open signal: auto once per key, or on demand from the account chip ------- */
let openTick = 0;
const openListeners = new Set<() => void>();
export function showCapabilities(): void {
  openTick++;
  for (const l of openListeners) l();
}
function useOpenSignal(): number {
  return useSyncExternalStore(
    (cb) => {
      openListeners.add(cb);
      return () => openListeners.delete(cb);
    },
    () => openTick,
  );
}

type Probe = "checking" | "yes" | "no" | "unknown";

// Persist which key we've already auto-shown the panel for, so a page REFRESH doesn't
// re-pop it — only an initial connect does. Stored as a short non-reversible hash (not the
// raw key). Cleared on disconnect so reconnecting genuinely re-shows it.
const SEEN_KEY = "gb.caps.seen";
function getSeen(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}
function setSeen(v: string | null): void {
  try {
    if (v) localStorage.setItem(SEEN_KEY, v);
    else localStorage.removeItem(SEEN_KEY);
  } catch {
    /* ignore */
  }
}

export function CapabilitiesModal() {
  const identity = useAuthIdentity();
  const [open, setOpen] = useState(false);
  const manualTick = useOpenSignal();
  const firstTick = useRef(manualTick);

  // Auto-open ONCE per credential — on the initial connect, not on refresh. `getSeen()`
  // persists across reloads; disconnect clears it so a fresh reconnect shows it again. The
  // identity ("sdk:<installId>" / "manual") is already non-secret, so we store it directly.
  useEffect(() => {
    if (!identity) {
      setSeen(null);
      setOpen(false);
      return;
    }
    // Don't auto-pop over the first-run welcome tour — let that finish first (the tour points
    // users at the ⓘ to open this on demand). Once the tour's seen, normal auto-open resumes.
    if (!tourSeen()) return;
    if (getSeen() !== identity) {
      setSeen(identity);
      setOpen(true);
    }
  }, [identity]);

  // Manual re-open (account chip → "what can this key do?").
  useEffect(() => {
    if (manualTick !== firstTick.current) {
      firstTick.current = manualTick;
      if (identity) setOpen(true);
    }
  }, [manualTick, identity]);

  if (!open || !identity) return null;
  return <CapabilitiesCard onClose={() => setOpen(false)} />;
}

function CapabilitiesCard({ onClose }: { onClose: () => void }) {
  const { data, isLoading, isError } = useAuthMe();
  const relay = useRelayStatus();


  // Stream probe — the app subscribes the orders channel app-wide, so the relay is already
  // trying to connect. "connected" ⇒ the key has stream:realtime; if it hasn't gone live
  // within a few seconds, treat it as off.
  const [streamTimedOut, setStreamTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStreamTimedOut(true), 5000);
    return () => clearTimeout(t);
  }, []);
  const stream: Probe = relay.display === "connected" ? "yes" : streamTimedOut ? "no" : "checking";

  const key = data?.current_key;
  const canTrade = data?.can_execute === true;
  const rpm = key?.rate_limit_per_minute ?? null;
  const rpd = key?.rate_limit_per_day ?? null;
  const credits = data?.credits?.total ?? null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-border bg-panel-2 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-base font-semibold text-ink">What this key can do</h2>
          <button onClick={onClose} className="text-faint hover:text-ink" title="Close">
            ✕
          </button>
        </div>

        {isLoading ? (
          <p className="py-6 text-center text-[13px] text-faint">Checking your key…</p>
        ) : isError || !data ? (
          <p className="py-6 text-center text-[13px] text-down">
            Couldn’t read this key (auth/me failed). It may be invalid or expired — try reconnecting.
          </p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2 text-[12px]">
              <span className="font-medium text-ink">{key?.name ?? key?.key_prefix ?? "your key"}</span>
              {key?.key_type && (
                <span className="rounded border border-border-2 px-1.5 py-0.5 text-[10px] uppercase text-dim">
                  {key.key_type}
                </span>
              )}
            </div>

            <div className="divide-y divide-border/60">
              <Row state="yes" label="View market data" hint="Contests, live liquidity, depth, prices, your account." />
              <Row
                state={canTrade ? "yes" : "no"}
                label="Place orders"
                hint={canTrade ? "Paper and real orders (real is off until you enable it in ⚙)." : "This is a read-only key — you can watch but not trade."}
                upgradeIf={!canTrade}
              />
              <Row
                state={stream}
                label="Live streaming"
                hint={
                  stream === "yes"
                    ? "Real-time liquidity and order pushes."
                    : stream === "no"
                      ? "No real-time stream on this key (needs the stream:realtime entitlement). Data still refreshes on a timer."
                      : "Checking the live connection…"
                }
                upgradeIf={stream === "no"}
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-faint">
              {(rpm != null || rpd != null) && (
                <span>
                  Rate budget:{" "}
                  <span className="text-dim">
                    {rpm != null ? `${rpm.toLocaleString()}/min` : "—"}
                    {rpd != null ? ` · ${rpd.toLocaleString()}/day` : ""}
                  </span>
                </span>
              )}
              {credits != null && (
                <span>
                  Credits: <span className="text-dim">{credits.toLocaleString()}</span>
                </span>
              )}
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-faint">
              Manage your key, plan, and venue connections at{" "}
              <a href="https://app.openmarkets.ai" target="_blank" rel="noreferrer" className="underline hover:text-dim">
                app.openmarkets.ai
              </a>
              . Your key is stored only in this browser.
            </p>

            <button
              onClick={onClose}
              className="mt-4 w-full rounded bg-up/20 px-3 py-2 text-[13px] font-semibold text-up hover:bg-up/30"
            >
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Row({
  state,
  label,
  hint,
  upgradeIf,
}: {
  state: Probe;
  label: string;
  hint: string;
  upgradeIf?: boolean;
}) {
  const icon =
    state === "yes" ? (
      <span className="text-up">✓</span>
    ) : state === "no" ? (
      <span className="text-down">✗</span>
    ) : state === "unknown" ? (
      <span className="text-amber">?</span>
    ) : (
      <span className="inline-block h-3 w-3 animate-spin rounded-full border border-faint border-t-transparent" />
    );
  return (
    <div className="flex items-start gap-2.5 py-2">
      <span className="mt-0.5 grid h-4 w-4 shrink-0 place-items-center text-[13px]">{icon}</span>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-[13px] font-medium ${state === "no" ? "text-dim" : "text-ink"}`}>{label}</span>
          {upgradeIf && (
            <a
              href="https://app.openmarkets.ai"
              target="_blank"
              rel="noreferrer"
              className="rounded bg-amber/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber hover:bg-amber/25"
            >
              Upgrade
            </a>
          )}
        </div>
        <p className="mt-0.5 text-[11px] leading-snug text-faint">{hint}</p>
      </div>
    </div>
  );
}
